import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/auth-context";
import { api } from "@/lib/api";

export function useIdleSpotReaper() {
  return useQuery({
    queryKey: ["admin-settings", "idle-spot-reaper"],
    queryFn: () => api.getIdleSpotReaper(),
  });
}

export function useSetIdleSpotReaper() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => api.setIdleSpotReaper(enabled),
    onSuccess: (data) => queryClient.setQueryData(["admin-settings", "idle-spot-reaper"], data),
  });
}

export function useNightlyReaper() {
  return useQuery({
    queryKey: ["admin-settings", "nightly-reaper"],
    queryFn: () => api.getNightlyReaper(),
  });
}

export function useSetNightlyReaper() {
  const queryClient = useQueryClient();
  return useMutation({
    // Partial patch: the server leaves an omitted field alone, so flipping one
    // toggle can never silently re-arm the other.
    mutationFn: (patch: { enabled?: boolean; dry_run?: boolean }) => api.setNightlyReaper(patch),
    onSuccess: (data) => queryClient.setQueryData(["admin-settings", "nightly-reaper"], data),
  });
}

export function useSpotPoolAccess() {
  return useQuery({
    queryKey: ["admin-settings", "spot-pool-access"],
    queryFn: () => api.getSpotPoolAccess(),
  });
}

export function useSetSpotPoolAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (openToAll: boolean) => api.setSpotPoolAccess(openToAll),
    onSuccess: (data) => queryClient.setQueryData(["admin-settings", "spot-pool-access"], data),
  });
}

export function useNebiusRegions() {
  return useQuery({
    queryKey: ["admin-settings", "nebius-regions"],
    queryFn: () => api.getNebiusRegions(),
  });
}

export function useSetNebiusRegions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: string[]) => api.setNebiusRegions(enabled),
    onSuccess: (data) => {
      queryClient.setQueryData(["admin-settings", "nebius-regions"], data);
      // The pool combo picker lists only combos in enabled regions.
      void queryClient.invalidateQueries({ queryKey: ["available-combos"] });
    },
  });
}

/**
 * Whether the current user may mutate spot targets.
 *
 * Admins always may, answered synchronously so they never see a flash of
 * hidden controls. For everyone else the answer depends on the
 * `spot-pool-access` admin setting, so it is `undefined` until that query
 * resolves — distinct from a resolved `false`, which means genuinely denied.
 *
 * Call sites currently render no mutation control for either `undefined` or
 * `false` (a `{canEdit && ...}` guard skips both; the enabled switch renders
 * disabled), so a non-admin sees the controls appear rather than disappear.
 * Keeping the two states apart lets a call site that cares render a distinct
 * loading placeholder while the setting is in flight.
 */
export function useCanEditSpotPool(): boolean | undefined {
  const { user } = useAuth();
  const { data, isPending } = useSpotPoolAccess();
  if (user?.is_admin) return true;
  if (isPending) return undefined;
  return data?.open_to_all ?? false;
}
